import {NgModule} from "@angular/core";
import {RouterModule, Routes} from "@angular/router";
import {DynamicComponent} from "./dynamic/dynamic.component";
import {GoogleAnalyticsGuard} from "./core/_guards/ga.guard";
import {CancelFeeComponent} from "./cancel-fee/cancel-fee.component";
import {UnsubscribeComponent} from "./unsubscribe/unsubscribe.component";
import {NotfoundComponent} from "./notfound/notfound.component";
import {LoginasComponent} from "./loginas/loginas.component";
import {CarvanaGuard} from './core/_guards/carvana.guard';
import {environment} from '../environments/environment';
import {AppointmentsOrQuotesGuard} from "./core/_guards/appointments-or-quotes.guard";
import {SiteMaintenanceComponent} from './site-maintenance/site-maintenance.component';
import {ZipcheckComponent} from "./zipcheck/zipcheck.component";
import {LandingPageDetailGuard} from "./core/_guards/landing-page-detail.guard";
import { FirestoneComponent } from "./firestone/firestone.component";
import {YmRedirectComponent} from "./ym-redirect/ym-redirect.component";
import {YMGuard} from "./core/_guards/ym.guard";
import {DISABLE_INVOICE_PAGE} from "./core/constants";
import {AuthGuard} from "./core/_guards/auth.guard";
import {LOGIN_STEPS} from "./shared/_components/login/login";

const routes: Routes = <Routes>[
    // Eager loaded pages
    {path: '', pathMatch: 'full', redirectTo: 'login'},
    {path: 'forgotpassword', pathMatch: 'full', redirectTo: 'login/forgotpassword'},
    {path: 'login', loadChildren: () => import('app/login-page/login-page.module').then(m => m.LoginPageModule)},
    {path: 'book-redirect', component: YmRedirectComponent},
    {path: 'unsubscribe', component: UnsubscribeComponent},
    {path: 'loginas/:token/:jobId', component: LoginasComponent},
    {path: 'loginas/:token', component: LoginasComponent},
    {path: 'loginas', component: LoginasComponent},
    {path: 'site-maintenance', component: SiteMaintenanceComponent},
    {path: 'firestone', component: FirestoneComponent},

    // Lazy Loaded Modules
    {path: 'firestone/:source/:zip/:bannerId', component: FirestoneComponent},
    {path: 'account', loadChildren: () => import('app/account/account.module').then(m => m.AccountModule)},
    {path: 'benefits', loadChildren: () => import('app/benefits/benefits.module').then(m => m.BenefitsModule)},
    {path: 'discounts', loadChildren: () => import('app/discounts/discounts.module').then(m => m.DiscountsModule)},
    {path: 'ppm', loadChildren: () => import('app/ppm/ppm.module').then(m => m.PpmModule)},
    {path: 'feedback', loadChildren: () => import('app/feedback/feedback.module').then(m => m.FeedbackModule)},
    {path: 'forgotpassword/:email', loadChildren: () => import('app/forgot-password/forgot-password.module').then(m => m.ForgotPasswordModule)},
    {path: 'resetpassword', loadChildren: () => import('app/forgot-password/forgot-password.module').then(m => m.ForgotPasswordModule)},
    {path: 'activateaccount', loadChildren: () => import('app/activateaccount/activateaccount.module').then(m => m.ActivateAccountModule)},
    {path: 'activatebootstrappedaccount', loadChildren: () => import('app/activate-bootstrapped-account/activate-bootstrapped-account.module').then(m => m.ActivateBootstrappedAccountModule)},
    {path: 'appointments', loadChildren: () => import('app/appointments/appointments.module').then(m => m.AppointmentsModule)},
    {path: 'appointments/:jobId', loadChildren: () => import('app/appointments/appointments.module').then(m => m.AppointmentsModule)},
    {path: 'addpayment', loadChildren: () => import('app/add-payment/add-payment.module').then(m => m.AddPaymentModule)},
    {path: 'garage', loadChildren: () => import('app/car/car.module').then(m => m.CarModule)},
    {path: 'quotes', loadChildren: () => import('app/quotes/quotes.module').then(m => m.QuotesModule)},
    // {path: 'price', loadChildren: () => import('app/price-breakdown/price-breakdown.module').then(m => m.PriceBreakdownModule)},
    {path: 'history/:jobId', loadChildren: () => import('app/service-history/service-history.module').then(m => m.ServiceHistoryModule)},
    {path: 'book', loadChildren: () => import('app/booking-flow/booking-flow.module').then(m => m.BookingFlowModule), canActivate: [YMGuard]},
    {path: 'carvana', loadChildren: () => import('app/landing-page-detail/landing-page-detail.module').then(m => m.LandingPageDetailModule), canActivate: [CarvanaGuard]},
    {path: 'p/:dynamicurl', loadChildren: () => import('app/landing-page-detail/landing-page-detail.module').then(m => m.LandingPageDetailModule), canActivate: [GoogleAnalyticsGuard, YMGuard, LandingPageDetailGuard]},
    {path: 'mechanic/:profileUrl', loadChildren: () => import('app/mechanic-profile/mechanic-profile.module').then(m => m.MechanicProfileModule)},
    {path: 'newaccount', loadChildren: () => import('app/signup-page/signup-page.module').then(m => m.SignupPageModule)},
    {path: 'newaccount/:salesRep', loadChildren: () => import('app/signup-page/signup-page.module').then(m => m.SignupPageModule)},
    {path: 'notifications', loadChildren: () => import('app/notifications/notifications.module').then(m => m.NotificationsModule)},
    {path: 'fleetuser', loadChildren: () => import('app/fleet/fleet.module').then(m => m.FleetModule)},
    {path: 'fleet/dashboard', loadChildren: () => import('app/fleet-dashboard/fleet-dashboard.module').then(m => m.FleetDashboardModule), canActivate: [AuthGuard]},
    // {path: 'fleet/appointments', loadChildren: () => import('app/appointments/appointments.module').then(m => m.AppointmentsModule)},
    // {path: 'fleet/quotes', loadChildren: () => import('app/quotes/quotes.module').then(m => m.QuotesModule)},

    {path: 'fleet', loadChildren: () => import('app/fleet/fleet.module').then(m => m.FleetModule)},
    {path: 'getmeouttahere', loadChildren: () => import('app/appointments/appointments.module').then(m => m.AppointmentsModule), canActivate: [AppointmentsOrQuotesGuard]},

    {path: 'newservice/selectlocation/:jobRequestId', redirectTo: '/quotes/:jobRequestId'},
    {path: 'newservice', redirectTo: 'book'},
    {path: 'newuser', redirectTo: 'book'},

    {path: 'notfound', name: 'NotFound', component: NotfoundComponent},

    {path: 'dynamic/:token', component: DynamicComponent},

    {path: 'signup', redirectTo: 'newaccount'},
    {path: 'sign-up', redirectTo: 'login'}, // for a Your Mechanic link

    {path: 'car', redirectTo: 'garage'},

    {path: 'zipcheck', component: ZipcheckComponent},

    {path: 'silverrockwarranty', redirectTo: '/p/silverrockwarranty'},
    {path: 'sr', redirectTo: '/p/silverrockwarranty'},
    {path: 'silverockwarranty', redirectTo: '/p/silverrockwarranty'},
    {path: 'silverock', redirectTo: '/p/silverrockwarranty'},
    {path: 'silverrock', redirectTo: '/p/silverrockwarranty'},
    {path: 'gwc', redirectTo: '/p/gwc'},
    {path: 'carshield', redirectTo: '/p/carshield'},

    // redirect for invoice
    {path: 'account/invoices', redirectTo: DISABLE_INVOICE_PAGE ? 'account' : 'account/invoices'},
    {path: 'invoice/:invoiceId', redirectTo: DISABLE_INVOICE_PAGE ? 'account' : 'account/invoice/:invoiceId'},

    // these are legacy routes. Don't need to add future mechanics here
    {path: 'brent', redirectTo: 'mechanic/brentf'},
    {path: 'rich', redirectTo: 'mechanic/richc'},
    {path: 'trevor', redirectTo: 'mechanic/trevork'},
    {path: 'drew', redirectTo: 'mechanic/drews'},
    {path: 'ken', redirectTo: 'mechanic/kenw'},
    {path: 'rene', redirectTo: 'mechanic/renen'},
    {path: 'richard', redirectTo: 'mechanic/richardg'},
    {path: 'justin', redirectTo: 'mechanic/justinj'},
    {path: 'steve', redirectTo: 'mechanic/steveg'},
    {path: 'lee', redirectTo: 'mechanic/leeb'},
    {path: 'ray', redirectTo: 'mechanic/rayo'},
    {path: 'richardg', redirectTo: 'mechanic/richardg'},
    {path: 'glenn', redirectTo: 'mechanic/glennh'},
    {path: 'eddie', redirectTo: 'mechanic/eddiec'},

    // test mechanic for dev
    // {path: 'test', redirectTo: 'mechanic/david'},

    {path: 'cancelfee', component: CancelFeeComponent},

    { path: '**', redirectTo: '/notfound' }

];

const mainRoute: Routes = <Routes>[
    {path: '', canActivate: [GoogleAnalyticsGuard], children: routes}
];

@NgModule({
    // Swap commented line to enable route tracing
    // imports: [RouterModule.forRoot(mainRoute, { useHash: environment.app, relativeLinkResolution: 'legacy' , enableTracing: true})],
    imports: [RouterModule.forRoot(mainRoute, { useHash: environment.app, relativeLinkResolution: 'legacy'})],
    exports: [RouterModule]
})

export class AppRoutingModule {

} // AppRoutingModule
